/**
 * Saved Users Page (Favourite people)
 *
 * Displays users the current user has saved as favourites, in a 3-column
 * avatar grid. Mirrors Flutter's SavedUsersScreen
 * (lib/presentation/screens/user/saved_users_screen.dart:173-218).
 *
 * Architecture: Page -> useSavedUsers Hook -> UseCase -> Service
 *
 * Features:
 * - FlexibleHeaderList with the `star.png` icon background
 * - 3-column grid of user avatars
 * - Infinite scroll pagination (10 IDs / page — see
 *   `FIRESTORE_WHERE_IN_MAX_VALUES` in serverFetchConstants; the page size
 *   IS the Firestore `where in` cap, so the page-fetch service issues a
 *   single batch query per page. Deliberate web divergence from Flutter's
 *   `favoriteUsersListPagination = 9`.)
 * - Reactive add/remove when saves toggle elsewhere in the app
 * - Loading, empty, and error states
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { UserAvatarGridItem } from '@/presentation/components/common/user/UserAvatarGridItem';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { useSavedUsers } from './hooks/useSavedUsers';

export const SavedUsersPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const gridColumnsCount = useGridColumnsCount();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { users, isLoading, isLoadingMore, hasMore, error, init, loadMore } =
    useSavedUsers();

  useBasePage({
    screenKey,
    onInit: () => {
      void init();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  const handleBack = () => {
    Navigator.goBack();
  };

  const handleUserClick = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderUserItem = useCallback(
    (user: UserEntity) => (
      <UserAvatarGridItem
        userId={user.firebaseId}
        name={user.name}
        onClick={handleUserClick}
        qa={{ id: `saved-user-${user.firebaseId}` }}
      />
    ),
    [handleUserClick],
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'saved-users', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'saved-users-list' }}
      items={users}
      renderItem={renderUserItem}
      getItemKey={(user) => user.firebaseId}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          errorText={error ? l10n.savedUsers.errorLoading : undefined}
          emptyText={l10n.savedUsers.empty}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.accountInfo.favouritePeople,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              iconPath="/assets/images/star.png"
              title={l10n.accountInfo.favouritePeople}
            />
          ),
        },
      }}
    />
  );
};
